import Badge from '@amzn/awsui-components-core/badge';
import Icon from '@amzn/awsui-components-core/icon';
import type { ProxySearchResultActions, ProxySearchResultBadges, SearchResult } from '@amzn/das-finsuite-shared-ui';
import { GlobalSearchBar as SharedGlobalSearchBar } from '@amzn/das-finsuite-shared-ui';
import { faStar as regularStar } from '@fortawesome/free-regular-svg-icons';
import { faExternalLinkAlt, faStar as solidStar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo, useRef, useState } from 'react';
import { AppLogo } from 'src/components/generic-components/AppLogo';
import type { UseEmbeddedSearchReturn } from 'src/hooks/useEmbeddedSearch';
import DFP_LOGO from '../../images/finsuite-icon.svg';
import { LogoConfig } from 'src/components/context/AppContextModels';
import { MAX_RESULTS_PER_CATEGORY, ProductFilterTag } from './GlobalSearchUtils';

// ─── Type Guards ──────────────────────────────────────────────────────────────

function isLogoConfig(value: unknown): value is LogoConfig {
  return value !== null && typeof value === 'object' && 'logoType' in (value as Record<string, unknown>);
}

function hasProxyBadges(value: unknown): value is ProxySearchResultBadges {
  return value !== null && typeof value === 'object';
}

function hasProxyActions(value: unknown): value is ProxySearchResultActions {
  return value !== null && typeof value === 'object';
}

// ─── Native Mode Renderers ────────────────────────────────────────────────────

export const createResultActionsRenderer = (toggleFavorite: (e: React.MouseEvent, result: SearchResult) => void) => {
  return (result: SearchResult): React.ReactNode => {
    if (result.type === 'products') {
      const logoConfig = isLogoConfig(result.logoConfig) ? result.logoConfig : null;
      const hasLogo = logoConfig && (logoConfig.selectedLogoName || logoConfig.s3Location);
      const renderIcon = () => {
        if (hasLogo) {
          return (
            <span className="global-search__embedded-icon" aria-label="App logo">
              <AppLogo logoConfig={logoConfig} />
            </span>
          );
        }
        if (result.embeddingEnabled) {
          return (
            <span className="global-search__embedded-icon" aria-label="Opens within FinSuite">
              <img src={DFP_LOGO} alt="" />
            </span>
          );
        }
        return null;
      };
      return (
        <div className="global-search__result-actions">
          {renderIcon()}
          <button
            className="global-search__favorite-btn"
            onClick={(e) => toggleFavorite(e, result)}
            aria-label={result.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            aria-pressed={!!result.isFavorite}
          >
            <FontAwesomeIcon icon={result.isFavorite ? solidStar : regularStar} className={result.isFavorite ? 'global-search__star--active' : ''} />
          </button>
        </div>
      );
    }
    if (result.type === 'announcements' || result.externalUrl) {
      return (
        <span className="global-search__external-icon" aria-label="Opens in new tab">
          <Icon name="external" />
        </span>
      );
    }
    return null;
  };
};

export const createFilterTagsRenderer = (
  glAccountTags: ProductFilterTag[],
  selectedGlAccount: string,
  setSelectedGlAccount: (label: string) => void
) => {
  return (sectionTitle: string): React.ReactNode => {
    if (sectionTitle !== 'Products' || glAccountTags.length === 0) return null;
    return (
      <div className="global-search__tags">
        {glAccountTags.map((tag) => (
          <span
            key={tag.label}
            className="global-search__tag"
            onClick={() => setSelectedGlAccount(tag.label)}
            role="button"
            tabIndex={0}
            aria-pressed={selectedGlAccount === tag.label}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setSelectedGlAccount(tag.label);
              }
            }}
          >
            <Badge
              color={selectedGlAccount === tag.label ? 'blue' : 'grey'}
              style={
                selectedGlAccount !== tag.label
                  ? { root: { background: 'transparent', borderColor: '#8d99a8', borderWidth: '1px', color: '#c9d1d9' } }
                  : undefined
              }
            >
              {tag.label} • {Math.min(tag.count, MAX_RESULTS_PER_CATEGORY)}
            </Badge>
          </span>
        ))}
      </div>
    );
  };
};

// ─── Proxy Mode ───────────────────────────────────────────────────────────────

const proxyResultBadge = (result: SearchResult): React.ReactNode => {
  const badges = hasProxyBadges(result.badges) ? result.badges : null;
  if (!badges?.typeBadge) return null;
  return <span className={`global-search__result-badge global-search__result-badge--${badges.typeBadge.variant}`}>{badges.typeBadge.label}</span>;
};

const createProxyResultActionsRenderer = (embeddedSearch: UseEmbeddedSearchReturn) => {
  return (result: SearchResult): React.ReactNode => {
    const actions = hasProxyActions(result.actions) ? result.actions : null;
    const badges = hasProxyBadges(result.badges) ? result.badges : null;
    return (
      <>
        {badges?.statusBadge && <Badge color="blue">{badges.statusBadge.label}</Badge>}
        {badges?.confidential && <Badge color="grey">Confidential</Badge>}
        {actions?.externalLink && (
          <span
            className="global-search__external-link-btn"
            onClick={(e) => {
              e.stopPropagation();
              window.open(actions.externalLink?.url, '_blank', 'noopener,noreferrer');
            }}
            role="link"
            aria-label="Open external link"
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} />
          </span>
        )}
        {actions?.favorite && (
          <span
            className={`global-search__favorite-btn ${actions.favorite.isFavorited ? 'global-search__favorite-btn--active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              embeddedSearch.sendAction(result.id, 'toggle-favorite');
            }}
            role="button"
            aria-label={actions.favorite.isFavorited ? 'Remove from favorites' : 'Add to favorites'}
            aria-pressed={actions.favorite.isFavorited}
          >
            <FontAwesomeIcon icon={actions.favorite.isFavorited ? solidStar : regularStar} />
          </span>
        )}
      </>
    );
  };
};

interface ProxySearchBarProps {
  embeddedSearch: UseEmbeddedSearchReturn;
}

export const ProxySearchBar: React.FC<ProxySearchBarProps> = ({ embeddedSearch }) => {
  const proxyCapabilities = embeddedSearch.capabilities!;
  const [proxyCategory, setProxyCategory] = useState<string>('all');
  const proxyQueryRef = useRef('');

  const renderActions = useMemo(() => createProxyResultActionsRenderer(embeddedSearch), [embeddedSearch]);

  return (
    <SharedGlobalSearchBar
      variant={proxyCapabilities.variant}
      placeholder={proxyCapabilities.placeholder}
      sections={embeddedSearch.sections}
      onQueryChange={(q) => {
        proxyQueryRef.current = q;
        setProxyCategory('all');
        embeddedSearch.sendQuery(q);
      }}
      onResultClick={(result) => embeddedSearch.sendResultClick({ path: result.path, externalUrl: result.externalUrl })}
      minQueryLength={2}
      categories={proxyCapabilities.categories}
      activeCategory={proxyCategory}
      onCategoryChange={(cat) => {
        setProxyCategory(cat);
        embeddedSearch.sendQuery(proxyQueryRef.current, cat);
      }}
      renderResultBadge={proxyResultBadge}
      renderResultActions={renderActions}
      highlightQuery
    />
  );
};
